import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Typography } from '@mui/material';
import PayrollService, { PayrollRun, describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import DataTable, { Column } from '../../components/ui/DataTable';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState } from '../../components/ui/States';
import PayrollPanel from './PayrollPanel';
import {
  count, money, percent, weekLabel,
} from './payrollUI';

// Every week on file, newest first. A row opens the week.
const PayrollHistory: React.FC = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await PayrollService.runs());
    } catch (err) {
      setError(describeError(err, 'Could not load the weeks.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const columns: Column<PayrollRun>[] = [
    {
      key: 'week',
      header: 'Week',
      sortValue: (r) => r.weekEnd,
      render: (r) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{weekLabel(r.weekStart, r.weekEnd)}</Typography>
          <Typography variant="caption" color="text.secondary">{r.source.fileName || 'Statement'}</Typography>
        </>
      ),
    },
    { key: 'status', header: 'Status', sortValue: (r) => r.status, filter: { choices: (r) => r.status, labels: { finalized: 'Finalized', draft: 'Draft' } }, render: (r) => <ToneChip tone={r.status === 'finalized' ? 'ok' : 'warn'} label={r.status === 'finalized' ? 'Finalized' : 'Draft'} /> },
    { key: 'people', header: 'Paid', align: 'right', numeric: true, hide: 'phone', sortValue: (r) => r.totals.paid, filter: 'number', render: (r) => r.totals.paid },
    { key: 'dispatches', header: 'Dispatches', align: 'right', numeric: true, hide: 'tablet', sortValue: (r) => r.totals.dispatches, filter: 'number', render: (r) => count(r.totals.dispatches) },
    { key: 'stops', header: 'Stops', align: 'right', numeric: true, hide: 'tablet', sortValue: (r) => r.totals.stops, filter: 'number', render: (r) => count(r.totals.stops) },
    { key: 'gross', header: 'Gross', align: 'right', numeric: true, sortValue: (r) => r.totals.gross, filter: 'number', render: (r) => <strong>{money(r.totals.gross)}</strong> },
    { key: 'net', header: 'Statement net', align: 'right', numeric: true, hide: 'phone', sortValue: (r) => r.statement.netAmountDue, filter: 'number', render: (r) => money(r.statement.netAmountDue) },
    { key: 'ratio', header: 'Ratio', align: 'right', numeric: true, sortValue: (r) => r.ratio, filter: 'number', render: (r) => percent(r.ratio) },
  ];

  return (
    <PageBody>
      <PayrollPanel onUploaded={() => load()} />
      <PageHeader title="History" />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <DataTable
        columns={columns}
        rows={runs}
        rowKey={(r) => r._id}
        loading={loading}
        onRowClick={(r) => navigate(`/payroll/weeks/${r._id}`)}
        empty={<EmptyState title="No weeks yet" compact />}
      />
    </PageBody>
  );
};

export default PayrollHistory;
